define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/dom-construct",
	"dojo/sniff",
	"dojo/keys",
	"dojo/query", // query
	"dojo/promise/all",
	"dojo/store/Memory",
	"dojo/string", // string.substitute
	"dojo/when",
	"dijit/focus",
	"dijit/form/_TextBoxMixin",
	"dijit/registry",
	"lodash",
	"./FilteringSelect",
	"./_ComboBoxMixin",
	"./_ComboBoxCheckedMixin",
	"./_ComboBoxMenu",
	"./_ErrorMessageMixin",
	"./_HasDropDownMixin",
	"csi",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/middleware/DelayFilteringService",
	"csi/store/common",
	"csi/store/DelayedFilteringRequest",
	"csi/store/DelayedRequest",
	"csi/store/QueryResults",
	"csi/xml",
	"onscanjs"
], function(declare, lang, domClass, domConstruct, has, keys, query, all, Memory, string, when, focus, _TextBoxMixin, registry, lodash, FilteringSelect,
	_ComboBoxMixin, _ComboBoxCheckedMixin, _ComboBoxMenu, _ErrorMessageMixin, _HasDropDownMixin, csi, _, html, locale,
	{createCustomFilterValue, excludeCustomFilters, generateHybridItems, generateRecentValues, isCustomFilterInRecentValues},
	{DISABLED_PROPERTY, EMPTY_GROUP_ITEM_PROPERTY, INVALID_PROPERTY}, {DelayedFilteringRequest}, {DelayedRequest}, QueryResults, xml, onScan) {

	// DOJO_INTERNAL
	// Checked MultiSelect filtered drop down be used to select multiple items at a time and add new items to the store on the fly
	//----------------------------------Parameters and functions--------------------------
	// defaultDisplayValue  : "Select"  /*String*/ ------- optional parameter used for declaring the initial displayed value if nothing is selected
	// defaultAll           : /*Boolean*/ --------- replaces "None" by "All" and if it is selected returns the whole values of store instead of an empty array
	// addItemAll           : /*Boolean*/ --------- adds "All" as an exclusive item, if selected returns all the values of the store
	// defaultClearText     : "SelectNone" /*String*/ ------- optional Parameter used to declare the clear all checkbox text which will be the first item.
	// addNewItem           : false /*Boolean*/ ------ optional Parameter used to disable the add new item capability to the store, default value is true.
	// classAttr            : /*string*/ ----- optional Store Header parameter that which tells the widget which store item attribute to look at to add a class to menu item
	// excludeValues        : /*string Array*/ ----- optional , if any values need to be excluded from the drop down list declare the values as an array
	// showMatchEntry       : false /*Boolean*/ ----- optional parameter that determines whether to show the typed entry matched on drop down as a new item or not, works only with addNewItem: true.
	// store                : /*Dojo Memory store*/ ------ required Parameter that supplies the widget with the store values. all of the values has to declared in field "id" inside the store.
	//                                     the "id" is the ones that will be returned once selections are done
	// searchAttr           : /*String*/ ------ required parameter that decides which data is displayed and searched by the widget.
	// placeHolderLabelAttr :/*String*/ ------ can be used to force which data parameter is used to display the text area selected value, if its different from labelAttr or searchAttr
	// value                : /*Array*/ ------ optional parameter that need to be supplied if initial selections are needed, this is an array of id values of the store., this value should be there in the store.
	// pageSize             : /*int*/ ------ optional parameter that decides the pagination limits, default page size is 20.
	// .get('value')        : will give the current selected values of the widget for the hidden input field.
	// .getSelectedOption() : will give the current selected values of the widget.
	// resetSelected        : (/*Array*/values) this function will reset the selected values to parameters passed, can be used on escape key press;
	// exclusiveItems       : /*Object*/ -------- optional parameter, each property in the object is an exclusive item (should exist in store) that will toggle all other selected values if checked. Key of the object should be store item id and value should be store label
	// showClearField       : /*Boolean*/ --------- whether load the "None" or not
	//----------------------------------------------------------------------

	var _MultiSelectComboBoxMixin = declare([_ComboBoxMixin, _ComboBoxCheckedMixin], {

		// for inline edits delay 300 milliseconds to prevent double click from selecting items of drop downs automatic Jira: - C8-3489
		displayDelay: 300,

		labelFunc: function(item, store) {
			// summary:
			//		Computes the label to display based on the dojo.data store item.
			// item: Object
			//		The item from the store
			// store: dojo/store/api/Store
			//		The store.
			// returns:
			//		The label that the ComboBox should display
			// tags:
			//		private

			// Use toString() because XMLStore returns an XMLItem whereas this
			// method is expected to return a String (#9354).
			// Remove getValue() for 2.0 (old dojo.data API)

			if (item[EMPTY_GROUP_ITEM_PROPERTY]) {
				return item.name;
			} else if (item[this.store.idProperty] == this.CLEARVALUE) {
				return item[this.storeLabel];
			} else {
				return (item[this.labelAttr] === null || item[this.labelAttr] === undefined || item[this.labelAttr] == "") && (item[this.searchAttr] === undefined || item[this.searchAttr] == "" || item[this.searchAttr] === null) ? null : (store._oldAPI ? store.getValue(item, this.labelAttr || this.searchAttr) : item[this.labelAttr || this.searchAttr]).toString();  // String
			}
		},

		refreshOptionLabels: function() {
			// summary: refresh checkbox labels
			if (!this.dropDown || !this._opened) return;

			var store = this.store;

			query(".dijitCheckBox", this.dropDown.domNode).forEach(function(domNode) {
				var cb = registry.byNode(domNode);
				var id = cb.value;
				var item = store.get(id);
				if (item) {
					var labelNode = domNode.nextSibling;
					var labelObject = this._getMenuLabelFromItem(item);

					if (labelObject.html) {
						labelNode.innerHTML = labelObject.label;
					} else {
						html.clear(labelNode);
						html.text(labelNode, labelObject.label);
					}
				}
			}, this);
		},

		_startSearch: function( /*String*/ text) {
			// summary:
			//		Starts a search for elements matching text (text=="" means to return all items),
			//		and calls onSearch(...) when the search completes, to display the results.
			// reset the property flag to make sure the abort will get called
			// this flag will be set in "_onKey" as well
			this.keepSearchTimer = false;
			const {bDelayFiltering, bRecents, bShowHybridList} = this;
			const switchToDelayedFilteringStore = (text == "" && bDelayFiltering && !bShowHybridList);

			if (switchToDelayedFilteringStore) {
				this.store = this.enableDelayedFiltering(this.store);
			}

			var store = this.store;
			this.inherited(arguments);
			this.searchAttr = this.searchAttr || this.labelAttr;

			this._abortQuery();

			var _this = this;
			var pageSize = this.pageSize;
			var idProperty = store.idProperty;
			const nameProperty = store.nameProperty;

			// Setup parameters to be passed to store.query().
			// Create a new query to prevent accidentally querying for a hidden
			// value from FilteringSelect's keyField
			var query = lang.clone(this.query);
			var options = {
				start: 0,
				count: pageSize,
				queryOptions: {
					ignoreCase: this.ignoreCase,
					deep: true
				}
			};
			var qs = string.substitute(this.queryExpr, [text.replace(/([\\\*\?])/g, "\\$1")]);
			var q;
			var actions;
			var originalResults;
			var sortedResults;
			var self = this;

			/**
			 * this function uses storeOptions - options defined in Names.js -> _doQuery()
			 * to create group items, add them to the list, and sort items per group
			 * group items are disabled checkbox items with extra css class, can't be selected
			 * another Names.js function onResponse() added group attribute to each result, 
			 * and this function groupItems() will group results by this attribute
			 * @param {object} storeOptions - options from Names.js -> _doQuery()
			 * @param {Node[]} items  - list of items
			 * @return {Node[]} - grouped items
			 */
			function groupItems(storeOptions, items) {
				// this function code copied from store/GroupQueryEngine.js
				// there may be two server requests, find group options
				const group = storeOptions?.nextResponseOptions?.group || storeOptions?.group;
				// groupId is function(item, group), defined in store options, to retrieve group property of item
				// this function will only be defined if store has grouping capability
				const groupId = group ? group.id : "";
				// if we have grouping then add groups and sort groups and items
				if (groupId) {
					const cssProperty = store?.cssProperty;
					const idProperty = store?.idProperty || "id";
					const labelProperty = store?.labelProperty || "name";
					const searchProperty = store?.searchProperty || "search";
					const groupNameFunc = group?.nameFunc;
					const groupIsSuggestedFunc = group?.isSuggestedFunc;
					const groupSortFunc = group?.sortFunc;
					const groupIdFunc = groupId && function(item) {
						const value = groupId(item, group);
						return (value == null) ? "" : value;
					};

					const groups = csi.hashArray(items, groupIdFunc, null, null, true);
					let ids = _.keys(groups);

					// sort groups by name
					if (groupSortFunc) {
						ids = csi.sort(ids, (id) => groupSortFunc(id, group));
					} else if (!group || (group.sortFunc === undefined)) {
						ids = ids.sort();
					}

					let groupedItems = [];
					_.each(ids, function(id) {
						// if empty group name, then just show results
						if (id) {
							const label = groupNameFunc?.(id, group) ?? id;
							const obj = {
								[idProperty]: id,
								[_this.labelAttr]: label,
								[labelProperty]: label,
								[searchProperty]: label,
								[DISABLED_PROPERTY]: true,
							};

							if (cssProperty) {
								const suggestedCss = (groupIsSuggestedFunc?.(id, group)) ? " csi-grouped-selection-suggested" : "";
								obj[cssProperty] = `csi-grouped-selection${suggestedCss}`;
							}
							groupedItems.push(obj);
						}
						groupedItems = groupedItems.concat(groups[id]);
					});
					return groupedItems;
				} else {
					return items
				}
			}

			function getResults(storeOptions) {

				if (store.isDelayedSearch && store.isWaiting) {
					originalResults = store.originalResults;
				}

				// re-sort results if going back to first page
				var start = options.start;
				if (!start) {
					sortResults();
				}

				var total = sortedResults.length;
				var items;

				if (start >= total) {
					items = [];
				} else {
					// sortedResults does not contain actual items, so map them back to original store
					items = _.map(sortedResults.slice(start, Math.min(start + pageSize, total)), function(item) {
						return (item[EMPTY_GROUP_ITEM_PROPERTY] || (item.id == csi.DIALOG_URL)) ? item : store.get(item.id);
					});
					// if custom values are allowed and filtering text is entered - don't show "No results" placeholder
					if ((items.length === 1) && items[0][EMPTY_GROUP_ITEM_PROPERTY] && self.addNewItem && text) {
						items.shift();
					}
				}

				const {bDelayFiltering, bPasting, bShowHybridList, checkedItems = [], recentValues, } = _this;
				const selectedItems = _this.getSelectedItems();
				const checkedValueCount = checkedItems.length;

				if (bShowHybridList && !text && checkedValueCount <= 10) {
					// hybrid mode (selected values + recent values + actual values)
					const {newProperty, cssProperty, labelProperty, newCss} = _this.store;
					const [finalSelectedItems, groupedRecentItems, groupedRestItems] = generateHybridItems(
						originalResults,
						items,
						selectedItems,
						recentValues,
						checkedItems,
						storeOptions,
						groupItems,
						locale,
						newProperty,
						cssProperty,
						labelProperty,
						newCss,
						(value, text, bNew) => _this.store.addSelectedValue(value, text, bNew),
						(item) => store.put(item)
					);
					self.groupedRecentItems = groupedRecentItems;

					// join them together
					items = finalSelectedItems.concat(groupedRecentItems).concat(groupedRestItems);
				} else if (bDelayFiltering && !bShowHybridList && !text) {
					// delayed filtering mode (selected values + recent values)
					const [finalSelectedItems, groupedNewRecentItems] = generateRecentValues(items, selectedItems, checkedItems, storeOptions, groupItems);

					// join them together
					items = finalSelectedItems.concat(groupedNewRecentItems);
				} else if (bDelayFiltering && bShowHybridList && !!text && !bPasting) {
					// user types in something to switch to the normal model, just keep the same behavior as before
					items = groupItems(storeOptions, items);
				} else {
					//  preserve pasted non exact values - newly created custom filters
					if (bPasting && self.addNewItem) {
						items = lodash.uniqBy(_this.selectedItems.concat(items), store.idProperty);
					}
					// attempt to group items
					items = groupItems(storeOptions, items);
				}

				if (bDelayFiltering && !!text) {
					// for the attributes we enable delayed filtering, once user types in something, we shouldn't display custom ones which we added them as part of the store in the function - generateHybridItems so that CMS would work properly
					items = excludeCustomFilters(items);
				}

				var results = QueryResults(items.concat(actions));
				results.displayMore = ((start + pageSize) < total);
				results.total = total;
				this.bPasting = false;

				// Setup method to fetching the next page of results
				results.nextPage = function(direction) {
					//	tell callback the direction of the paging so the screen
					//	reader knows which menu option to shout
					options.direction = (direction !== false);

					if (options.direction) {
						options.start += pageSize;
					} else {
						options.start -= pageSize;

						if (options.start < 0) {
							options.start = 0;
						}
					}

					_this.onSearch(getResults(), query, options);
				};

				return results;
			}

			function sortResults() {
				actions = [];
				_this._sortMap = _this._sortMap || {};

				var data = csi.transform(originalResults, function(item) {
					if (item[store.actionProperty]) {
						actions.push(item);
						return null;
					}

					var obj = {
						id: item[idProperty],
						name: item[nameProperty],
						check: _.contains(_this.value, item[idProperty]),
						sort: item[INVALID_PROPERTY] ? -1 : store.indexProperty ? item[store.indexProperty] : item[_this.searchAttr].toLowerCase(),
						[EMPTY_GROUP_ITEM_PROPERTY]: item[EMPTY_GROUP_ITEM_PROPERTY],
						[DISABLED_PROPERTY]: item[EMPTY_GROUP_ITEM_PROPERTY],
					};

					// store sort value to be used to sort selected items when returning widget value
					_this._sortMap[obj.id] = obj.sort;
					return obj;
				});

				var memoryStore = new Memory({data: data});

				// remove exclusive items from the store and keep them in the array to put back after the store is sorted
				var exclusiveArray = [];
				_.each(_this.exclusiveItems, function(val, key) {
					var item = memoryStore.get(key);
					if (item) {
						// make sure only exclusive items which are in the store are added to exclusive array
						exclusiveArray.push(item);
						memoryStore.remove(key);
					}
				});

				// filters have already been applied, so just sort; checked items go first if so configured
				const sortOptions = [{attribute: "sort", descending: false}];

				if (_this.showCheckedFirst) {
					sortOptions.unshift({attribute: "check", descending: true});
				}

				sortedResults = memoryStore.query(null, {sort: sortOptions});

				// concat sortedResults array with exclusiveArray to display exclusive items on top of the drop down list
				sortedResults = exclusiveArray.concat(sortedResults);
			}

			function startQuery() {
				// FIX: this is to prevent widget from using "query" API to query existing result set;
				// with introduction of ExtractValues, additional queries with different parameters would cause
				// new request to server

				if (switchToDelayedFilteringStore) {
					_this._resultStore = store;
				} else {

					// when user switches to the normal mode from recent values mode, use the loaded store, so that it won't issue another request when select the item
					if (store instanceof DelayedFilteringRequest) {
						store = store.switchToOriginalStore();
					}

					const _store = store._store || store;
					_this._resultStore = _store.sourceStore || _store;
				}

				var emptyValue = store.getEmptyValue ? store.getEmptyValue() : null;

				query[idProperty] = query[idProperty] || {
					test: function(value, item) {
						// filter the first "select all" or "None" empty from the results so its not added to the total count
						return (value !== emptyValue) && !!_this.labelFunc(item, _this._resultStore);
					}
				};

				// query store here which may cause request to server - do not include options for now; they will be included later
				let storeOptions = {ignoreCase: this.ignoreCase}

				if (bRecents && bDelayFiltering && !text) {
					// in the case of delayed filtering store, there's only one specific group
					// if delayed filtering is not enabled or user types in something, not need to group them
					storeOptions = {
						...storeOptions,
						group: {
							id: (item) => 1,
							nameFunc: (id) => locale.resource("Recents"),
						}
					}
				}

				if (this.bAdHoc) {
					storeOptions.quickSearchQuery = this.view.quickSearchQuery;
				}

				var resPromise = _this._fetchHandle = store.query(query, storeOptions);

				if (qs == _this._lastQuery) {
					// this is to fix the key enter prev / next after an item is clicked
					_this._lastQuery = q;
				}

				if (_this.disabled || _this.readOnly || (q !== _this._lastQuery)) {
					return;
				} // avoid getting unwanted notify

				when(resPromise, function(res) {
					originalResults = res;
					_this.set("aborted", false);
					_this._fetchHandle = null;

					// if this response does not have all the information, such as no counts, then wait for secondary response and then redraw results
					if (res.secondaryResponse) {
						when(res.secondaryResponse, function() {
							_this.refreshOptionLabels();
						});
					}

					_this.onSearch(getResults(storeOptions), query, options);
				}, function(err) {
					_this.set("aborted", true);
					_this._fetchHandle = null;

					if (!_this._cancelingQuery) { // don't treat canceled query as an error
						console.error(_this.declaredClass + ' ' + err.toString());
					}
				});
			}

			lang.mixin(options, this.fetchProperties);

			// Generate query
			if (store._oldAPI) {
				// remove this branch for 2.0
				q = qs;
			} else {
				// Query on searchAttr is a regex for benefit of dojo/store/Memory,
				// but with a toString() method to help dojo/store/JsonRest.
				// Search string like "Co*" converted to regex like /^Co.*$/i.
				q = this._patternToRegExp(qs);
				q.toString = function() {
					return qs;
				};

			}

			// set _lastQuery, *then* start the timeout
			// otherwise, if the user types and the last query returns before the timeout,
			// _lastQuery won't be set and their input gets rewritten
			this._lastQuery = query[this.searchAttr] = q;
			this._queryDeferHandle = this.defer(startQuery, this.searchDelay);
		}

	});

	return declare([FilteringSelect, _MultiSelectComboBoxMixin, _ErrorMessageMixin, _HasDropDownMixin], {
		required: false,

		/** @property {boolean} bAdHoc - this component is used in adhoc filter (should involve view.quickSearchQuery) */
		//bAdHoc: false,

		/** @property {boolean} bDelayFiltering - sets true if to enable delay filtering feature */
		bDelayFiltering: false,

		/** @property {boolean} bRecents - sets true if to enable recent value feature */
		bRecents: false,

		/** @property {boolean} bShowHybridList - sets to true to show both recent values and actual values initially */
		bShowHybridList: false,

		/** @property {boolean} bScanned - sets true if it detects a bar code line break character after scan*/
		bScanned: false,

		/** @property {boolean} bPasteBlanks - if true then empty lines in pasted multiline text treated as value blanks */
		// bPasteBlanks: false,

		/** @property {boolean} bPasteConcat - if true then pasted multiline values being concatinated with existing selection, otherwise it would replace */
		bPasteConcat: true,

		/** @property {boolean} bPasting - if true indicates user is pasting something to the filter */
		bPasting: null,

		/** @property {event} enterEvent - store enterkey events to be used with hand scanner inputs*/
		enterEvent: null,

		/** @property {string[]} groupedRecentItems - grouped recent items */
		groupedRecentItems: [],

		/** @property {boolean} keepSearchTimer - if true, the search will NOT be aborted */
		keepSearchTimer: false,

		/** @property {string[]} preGoToDialogValue - value of widget before Go to Dialog was clicked and value changed */
		preGoToDialogValue: null,

		/** @property {string[]} recentValues - hold the recent selections */
		recentValues: null,

		/** @property {boolean} showCheckedFirst - if true, then checked items will be shown before unchecked items */
		showCheckedFirst: true,

		/** @property {object} view - reference to main view to access view.quickSearchQuery (this component is used in adhoc filter) */
		//view: null

		// Boolean: set to true when query is aborted - widget is blurred before search is complete
		//aborted: false,

		// String: value to restore when widget goes invalid
		defaultDisplayValue: "Select",
		defaultClearText: "SelectNone",
		showClearField: true,

		//Array: a temporary storage location for this.value so it can be retraced if value get reset while selection is processed.
		checkedItems: null,
		CLEARVALUE: "__clear",
		ALLVALUE: "__all",
		widgetType: "multiSelect",
		defaultAll: false,
		addItemAll: false,
		_initAtt: false,
		_bSettingItemValue: false,
		initialSelection: true,
		storeLabel: null,
		selectAllLabel: "SelectAll",
		selectAllItemLabel: "ItemsSelectedAll",
		selectAllNoItemLabel: "ItemsSelected",
		showMatchEntry: false,
		validateType: null,
		placeHolderLabelAttr: null,
		excludeValues: [],
		defaultAllPosition: 0,

		// Boolean: Reset text on selection of item.
		// By default text will not be reset and user can continue to type after selection.
		resetTextOnSelect: false,

		//exclusiveItems list of those values including clear all and Non Blank
		exclusiveItems: null,

		// Number: number of all results in store when defaultAll is true
		//_defaultDisplayValueTotal: null,

		// Object: map of objects mapping item id to sort value
		//_sortMap: null,

		// String: boolean attribute to set on new store items
		//_newItemAttr: null,


		/**
		 * Add onscanjs to differentiate between a bar code scanner input and a keyboard input
		 */
		buildRendering: function() {
			this.inherited(arguments);
			var self = this;

			onScan.attachTo(this.textbox, {
				timeBeforeScanTest: 100,
				onKeyDetect: function(iKeyCode, oEvent) {
					if (iKeyCode == 13) {
						//onscanjs removes key enter events so store the key enter events to make a call later
						self.enterEvent = oEvent;
					}
				},
				onScan: function(sCode, iQty) { // Alternative to document.addEventListener('scan')
					if (self.enterEvent) {
						self.bScanned = true;
						self._onKey(self.enterEvent);
					}
				}
			});
		},

		compare: function(/*anything*/ val1, /*anything*/ val2) {
			// summary: override to work with arrays
			return _.isEqual(val1, val2) ? 0 : 1;
		},

		postCreate: function() {
			this.inherited(arguments);

			// If drop down is closed and text area is not focused, then open drop down on click
			var self = this;
			this.on(".dijitInputContainer:click", function(e) {
				if (!self._opened) {
					self._startSearch("");
				}
			});
			this.on("paste", lang.hitch(this, this._onPasteMultiline));
		},

		focus: function() {
			// summary: highlight text and start search when widget comes into focus if so configured
			this.inherited(arguments);

			if (!this._opened) {
				_TextBoxMixin.selectInputText(this.focusNode);
				this._setPlaceholderValue(true);
			}
		},

		onSelectOption: function() {
			// summary: extension point called when selecting an option that should trigger change immediately (such as Go to Dialog...)
		},

		getSelectedOption: function() {
			return this.value;
		},

		csiResetValue: function() {
			// summary: reset to last known good value
			var v = this._csiResetValue || this._resetValue;
			if (v) {
				v.sort();
			}
			this.set("value", v, false);
		},

		getErrorMessage: function(/*Boolean*/ /*===== isFocused =====*/) {
			// summary: override ValidationTextBox to appropriately test for selections
			var invalid = this.invalidMessage == "$_unset_$" ? this.messages.invalidMessage :
				!this.invalidMessage ? this.promptMessage : this.invalidMessage;
			var missing = this.missingMessage == "$_unset_$" ? this.messages.missingMessage :
				!this.missingMessage ? invalid : this.missingMessage;
			return (this.required && !this._getExternalValueAttr().length) ? missing : invalid;
		},

		isValid: function() {
			// summary: override FilteringSelect
			return !this.required || !!this._getExternalValueAttr().length;
		},

		onEscape: function() {
			// summary: extension point for when escape key used
		},

		onInput: function(evt) {
			// summary: reset widget to previous value when escape used
			if (evt.keyCode == keys.ESCAPE) {
				this.resetSelected(this._lastValueReported);
				this.onEscape();
			}
		},

		removeSelection: function(value) {
			var self = this;
			this.selectedItems = _.filter(this.selectedItems, function(item) {
				return item[self.store.idProperty] !== value;
			});
			this.value = _.difference(this.value, [value]);
			this.checkedItems = _.difference(this.checkedItems, [value]);
			this._setPlaceholderValue();
			this._setDropdownValues();
		},

		resetSelected: function(value) {
			if (value == null) {
				value = [""];
			} else if (!_.isArray(value)) {
				value = [value];
			}
			value = _.map(value, function(val) {
				return val.toString();
			});

			this.value = this.checkedItems = this._resetValue = this._csiResetValue = value;
			this._setCheckedValue();
			this._setPlaceholderValue();
			this._setDropdownValues();

			// clear item - don't do so using "set" function as that will also call "set" for value as below
			this.item = null;

			//set priorityChange so that onChangeHandle is called on _FormWidgetMixin
			this.set("value", this.value, true /*priorityChange*/, "", undefined, true);
		},

		_getExternalValueAttr: function() {
			//summary: returns the value of the widget based on the selection, ie if its from a form and "(All)" is selected
			//If (All) is checked it returns all the values of the store
			const {ALLVALUE, addItemAll, defaultAll, store} = this;
			let value = this.get('value');

			if (store && value && ((!value.length && defaultAll) || (value.length == 1 && addItemAll && value[0] == ALLVALUE))) {
				// can only get the value if store is already loaded - this function is sync, so it won't wait for the query to finish
				if (store.isLoaded()) {
					// loops through the store and get all values if (All) is the default item
					value = store.query()
						.filter((item) => !item[DISABLED_PROPERTY])
						.map(store.getIdentity, store);
				} else {
					value = [];
				}

				if (value.length) {
					value = _.without(value, csi.DIALOG_URL);

					if (addItemAll) {
						value = _.without(value, ALLVALUE);
					}
				}
			}
			return value;
		},

		getSelectedItems: function() {
			this._setCheckedValue();
			return this.selectedItems;
		},

		/**
		 * Update internal properties based on current value
		 * @param {boolean} fromStore - query results from current store rather than rely on past results
		 */
		_setCheckedValue: function(fromStore) {
			this.checkedValues = [];
			const oldSelectedItems = this.selectedItems;
			this.selectedItems = [];

			const {
				checkedValues,
				labelAttr,
				placeHolderLabelAttr,
				searchAttr,
				selectedItems,
				store,
				value,
				_resultStore
			} = this;

			const attr = placeHolderLabelAttr || labelAttr || searchAttr;
			const resultStore = _resultStore || (store && store._store);

			if (fromStore && store) {
				// this may do a new server request
				const _store = store.sourceStore || store;
				when(_store.query(), (results) => {
					const {idProperty} = store;
					const obj = csi.hashArray(results, idProperty);

					// not sure if this is a real case, but ensuring we don't add item with null id (check was in previous code)
					delete obj[null];

					for (const id of value) {
						const item = obj[id];
						if (item) {
							checkedValues.push(item[attr]);
							selectedItems.push(item);
						}
					}
				});
			} else if (resultStore) {
				for (const id of value) {
					// if item is not in the store - check to see if this is new custom value
					const item = resultStore.get(id) || oldSelectedItems?.filter(item=>(item._value === id) && item._new)[0];
					if (item) {
						checkedValues.push(item[attr]);
						selectedItems.push(item);
					}
				}
			}
		},

		_getAllItemText: function(n) {
			return locale.resource(n ? this.selectAllItemLabel : this.selectAllNoItemLabel, [n]);
		},

		_getDefaultDisplayText: function() {
			if (this._defaultDisplayValueTotal != null) {
				return this._getAllItemText(this._defaultDisplayValueTotal);
			}

			return this.defaultAll ? this._getSelectAllText() : locale.resource(this.defaultDisplayValue);
		},

		_getDefaultClearText: function() {
			return this.defaultAll ? this._getSelectAllText() : locale.resource(this.defaultClearText);
		},

		_getSelectAllText: function() {
			return locale.resource(this.selectAllLabel);
		},

		_setDefaultClearTextAttr: function(value) {
			this._set("defaultClearText", value);
			this._setPlaceholderValue();
		},

		_setDefaultDisplayValueAttr: function(value) {
			this._set("defaultDisplayValue", value);
			this._setPlaceholderValue();
		},

		/**
		 * sets the value dropdown widget
		 */
		_setDropdownValues: function() {
			if (this.dropDown) {
				this.dropDown.value = this.value;
				this.dropDown.selectedValues = this.checkedValues;
			}
		},

		/**
		 * Return placeholder label for current set of checked values; when the labelType is HTML, the checkedValues
		 * (which can come from DB) cannot just be used as labels as they may contain unsanitized HTML. The item labels
		 * must be used instead.
		 * @return {string}
		 */
		_convertCheckedValuesToPlaceholderLabel() {
			let {checkedItems, checkedValues, labelAttr, labelType, placeHolderLabelAttr, searchAttr, store} = this;

			if (store && (labelType == "html")) {
				const attr = placeHolderLabelAttr || labelAttr || searchAttr;
				const items = checkedItems.map((id) => store.get(id));

				if (_.compact(items).length === checkedItems.length) {
					checkedValues = items.map((item) => item[attr]);
				}
			}

			return checkedValues.join('\n');
		},

		/**
		* Return count items that are not disabled
		* @param {Memory} resultStore
		* @return {number}
		*/
		_getNumberOfEnabledItems(/*dojo/store/Memory*/ resultStore) {
			return _.filter(resultStore.data, function(item) {
				return !item._disabled;
			}).length;
		},

		_setPlaceholderValue: function(clear) {
			if (this.checkedItems && this.checkedItems.length && (!this.checkedValues || (this.checkedValues.length != this.checkedItems.length))) {
				this._setCheckedValue();
				if (this._lastQuery != null && this.checkedValues.length != this.checkedItems.length) {
					//checking _lastQuery will prevent server request if value required is already present on store
					this._setCheckedValue(true);
				}
			}

			const {
				store,
				_resultStore
			} = this;

			const resultStore = _resultStore || (store && store._store);
			var defaultDisplayText = this._getDefaultDisplayText();
			var placeHolderText;

			// value may be string or array. Array may contain empty string dummy member that we want to filter out
			const value = (this.value.filter && this.value.filter(item => item != "")) || this.value;

			switch (value.length) {
				case 0:
					placeHolderText = defaultDisplayText;
					break;

				case 1:
					if (!value[0] || !this.checkedValues) {
						placeHolderText = defaultDisplayText;
					} else if (this.labelType == "html") {
						const htmlValue = this._convertCheckedValuesToPlaceholderLabel();
						placeHolderText = domConstruct.create("div", {innerHTML: htmlValue}).textContent;
					} else {
						placeHolderText = this.checkedValues[0] || this.checkedItems[0];
					}

					if (this.addItemAll && value[0] == this.ALLVALUE) {
						if (!resultStore) {
							return;
						}

						// add store count to item "All"
						placeHolderText = this._getAllItemText(this._getNumberOfEnabledItems(resultStore));
					}
					break;

				default:
					placeHolderText = locale.resource("ItemsSelected", [value.length]);
					break;
			}

			this.displayedValue = placeHolderText;

			this.set("placeHolder", placeHolderText);

			// do not show any titles in multi-select fields.
			this.textbox.title = "";

			if (clear) {
				this.textbox.value = "";
			} else {
				if (focus.curNode == this.focusNode) {
					this._setCaretPos(this.focusNode, this.focusNode.value.length); // move cursor to end and cancel highlighting
				}
			}
		},

		_handleOnChange: function( /*anything*/ newValue, /*Boolean?*/ priorityChange) {
			// summary: track last valid change
			if (newValue != csi.DIALOG_URL) {
				// prevents setting of unwanted values during initialization before setting _onChangeHandle on _FormWidgetMixin
				if (((_.isArray(newValue) && newValue[0] != "") || !_.isArray(newValue)) && (priorityChange !== undefined || this._bSettingItemValue)) {
					this.inherited(arguments);
				}

				// use newValue if checkedItems is null or empty array
				if (this.checkedItems?.length) {
					this._csiResetValue = this.checkedItems;
				} else {
					// newValue may be empty or non empty array or also empty or non empty string. 
					// here we need to convert it to array as following: if newValue is array then keep it as is, 
					// if new value is non empty string, then convert it to array, and if empty string then to empty array
					this._csiResetValue = Array.isArray(newValue) ? newValue : (newValue ? [newValue] : []);
				}
				this.value = this._csiResetValue;
				this.valueNode.value = this.value;
				this._setPlaceholderValue(true);
			}
		},

		_onDropDownMouseDown: function( /*Event*/ e) {
			// summary: make sure widget in focus when clicking drop-down button (Ticket #13790)
			if (this.disabled || this.readOnly) return;
			if (this.dropDown && this._opened) {
				// closes the dropdown if its already opened
				this.closeDropDown()
				return;
			}
			this.inherited(arguments);
			if (this.dropDown && this.value && this.value.length && !this.dropDown.selectedValues) {
				//if values are not set on a new dropdown already, then set the dropdown values
				this._setDropdownValues();
			}
			this.focus();
			_TextBoxMixin.selectInputText(this.focusNode);
		},

		/**
		 * This function process paste event, parse pasted date and search for multiline text
		 * multiline text then used as a list of values to select
		 * pasted values which have no matching option will be ignored
		 * empty line treated as option "blanks" and being added to selection
		 * empty line in the middle or at start of the text will be treated as "blanks"
		 * single trailing "LineEnd" will be ignored
		 * double "LineEnd" at the end of the multiline text treated as "blanks" and added to selection
		 * @param { PasteEvent } e 
		 */
		_onPasteMultiline: function( /*Event*/ e) {
			let {store} = this;
			const dfds = [];
			if (store.bDelayedRequest) {
				store = store.sourceStore;
			}

			if (e.clipboardData && !(store instanceof DelayedRequest)) {
				// retrieve clipboard data, using plain text format
				const data = e.clipboardData.getData('text/plain');
				// convert multiline to array
				let dataArray = data && html.multilineTextToArray(data);
				// useBlanks=true if this control allows using "blanks"
				const useBlanks = store.get(xml.VALUE_BLANK);
				this.bPasting = true;
				// ignore and remove empty lines if control does not allows blanks
				// if user copied single item with EOL - it will be treated as single pasted item
				// do not count last line end as it caused by trailing "LineEnd"
				// if user intentionally adding empty line - there will be two "LineEnd" at the end after splitting
				if (useBlanks) {
					dataArray = dataArray.filter((item, idx) => (item || (idx < (dataArray.length - 1))));
				} else {
					dataArray = dataArray.filter(item => item);
				}
				const dataLength = dataArray.length;
				let newValuesSet = new Set();
				let hasBlanks = false;

				const stopEvent = (e) => { 
					// for single item paste we allow to continue editing
					if (dataLength === 1) {
						return;
					} 
					e.stopPropagation();
					e.preventDefault();
				}
			
				const updatePastedData = ()=>{
					// add pasted data to selection and close drop down
					if (newValuesSet.size) {
						// treat empty line as "VALUE BLANK"
						if (hasBlanks && this.bPasteBlanks) {
							newValuesSet.add(xml.VALUE_BLANK);
						}

						// preserve existing selection
						if (this.bPasteConcat && this.value) {
							newValuesSet = new Set([...newValuesSet, ...this.value]);
						}

						// dont use resetSelected here as it will update filter value and immediately filter the view
						this.value = this.checkedItems = this._resetValue = this._csiResetValue = [...newValuesSet];
						this._setCheckedValue();
						this._setPlaceholderValue();
						this._setDropdownValues();
			
						// for single item paste we do not need force update here as we do not stop event and control will do search and update itself
						if (dataLength > 1) {
							this.closeDropDown();

							// defer this function so that the selected items are processed and in place so that we can be able to render them 
							this.defer(() => this.loadAndOpenDropDown(), 200);
						}
						stopEvent(e);
					}
				}

				const processResult = (queryResultOriginal, pastedItem) => {
					const queryResultLength = queryResultOriginal.total;
					const existingItem = queryResultLength >= 1;
					// if query result return more than one item, this is the case when we have custom filter with the same 
					// label as real item, so filter out custom filter and try to use real item
					let queryResult = (queryResultLength > 1) ? queryResultOriginal.filter(item => !item._new) : queryResultOriginal;
					// if item exists then we have to use its value rather than label
					const pastedValue = existingItem && queryResult[0][store.idProperty]?.toString?.();

					let isCustomFilterOfRecentValues = false;

					// if not exist in the store, need to check if it exists in the recent values
					if (!existingItem) {
						isCustomFilterOfRecentValues = isCustomFilterInRecentValues(pastedItem, new Set(this.groupedRecentItems));
					}

					if (!existingItem && !isCustomFilterOfRecentValues && this.addNewItem) {
						const matchingValue = createCustomFilterValue(pastedItem);
						const newItem = {
							_count: 0,
							_refs: null,
							_textCount: pastedItem,
							_text: pastedItem,
							_value: matchingValue,
						};
						newItem[store.cssProperty] = store.newCss;
						newItem[store.newProperty] = true;

						store.put(newItem);
						// do not select non exact match for single paste
						if (dataLength > 1) {
							newValuesSet.add(matchingValue);
						}
					} else if (existingItem) {
						newValuesSet.add(pastedValue);
					} else if (isCustomFilterOfRecentValues) {
						// if it is custom filter or doesn't match any, add it as custom filter
						const matchingValue = createCustomFilterValue(pastedItem);
						newValuesSet.add(matchingValue);
					}
				}

				// parse and verify pasted data, ignore/exclude invalid input data
				dataArray.forEach((pastedItem, idx) => {
					// if user intentionally adding empty line and we can use blanks - add blank item
					if (useBlanks && (pastedItem === "")) {
						hasBlanks = true;
					}

					// if "hasBlanks" - empty value will be added later, otherwise ignore empty pasted item
					if (pastedItem == "") {
						return;
					}

					// check if pasted item has matching option
					const q = {};
					const property = store.searchProperty || store.labelProperty;
					const regex = new RegExp("^" + lodash.escapeRegExp(pastedItem) + "$", "i");
					q[property] = regex;
					
					const res = store.query(q);

					if (res.then) {
						// suspended view multiline paste case - store will issue request and we have to wait for results
						res.then(res=>processResult(res, pastedItem));
						dfds.push(res);
					} else {
						processResult(res, pastedItem);
					}
				});

				if (dfds.length) {
					// suspended view multiline paste case - store will issue request and we have to wait for results
					stopEvent(e);
					all(dfds).then(updatePastedData);
				} else {
					updatePastedData();
				}
			}
		},

		/**
		 * @override
		 * Execute this function as needed
		 * */
		_abortQuery: function() {
			if (has("safari") && this.keepSearchTimer) {
				return;
			}

			this.inherited(arguments);
		},

		_onKey: function(/*Event*/ evt) {
			// summary: Handles keyboard events
			this._highlightedKeyEvent = null;

			// alphanumeric reserved for searching
			if (evt.charCode >= 32) {
				return;
			}

			var key = evt.charCode || evt.keyCode;

			// throw out spurious events
			if (key == keys.ALT || key == keys.CTRL || key == keys.META || key == keys.SHIFT) {
				return;
			}

			// handle Ctrl+A or CMD+A to select all
			if (key == 65 && (evt.ctrlKey || evt.metaKey) && this.dropDown) {
				this.dropDown.checkAllItems();
				evt.stopPropagation();
				evt.preventDefault();
				return;
			}

			var pw = this.dropDown;
			var highlighted = null;

			if (this._opened) {
				highlighted = pw.getHighlightedOption();

				// User selected "Go To Dialog" and hit key ENTER
				if (highlighted && highlighted.type == csi.DIALOG_URL && (key == keys.ENTER)) {
					pw.m_selected = highlighted;
				}

				//store key event so it can be picked up later in _selectOption for ENTER key
				if (highlighted && highlighted != pw.nextButton && highlighted != pw.previousButton) {
					this._highlightedKeyEvent = key;
				}
			}

			// _HasDropDown will do some of the work:
			//
			//	1. when drop down is not yet shown:
			//		- if user presses the down arrow key, call loadDropDown()
			//	2. when drop down is already displayed:
			//		- on ESC key, call closeDropDown()
			//		- otherwise, call dropDown.handleKey() to process the keystroke
			if (this.dropDown && this.dropDown.items && this.dropDown.items.length < 1) {
				this._startSearch("");
			}

			// _abortQuery will be get called in _startSearch anyway, keep the timer so that the search continue running, the timer will be removed somehow in some cases
			// one interesting case(C8-15275) is that on Safari browser, when type in some numbers(1, 2, etc.) using input source(Chinese so far) other than English,
			// the keydown & keyup event didn't get fired, then the callback function for "_onKey" will be executed in advance where the timer will be removed, however, the
			// callback function for this event didn't get called yet
			this.keepSearchTimer = true;

			const isNextPrevButton = pw && (highlighted == pw.nextButton || highlighted == pw.previousButton);

			// toggle clicked value before call inherited (which may call on blur methods to set value)
			if ((key == keys.ENTER) || (key == keys.INSERT)) {
				if (!isNextPrevButton && highlighted && (this.dropDown != null)) {
					// Allow m_selected for keys.ENTER as well, which allows enter to be used in CMS drop down to select option
					this.dropDown.m_selected = this.dropDown.selected;
					this.dropDown.selectCurrentAttr();
					
					// use case: paste nonexisting value and use keyboard key "enter" to select it, then blur away
					if ((!this.value || !this.value.length) && (this.value != this.dropDown.value)) {
						this.value = this.dropDown.value;
					}
				}
			}

			const oldOpened = this._opened;
			// set _opened to false so the drop down wont be closed in _AutoCompleterMixin
			//if (key == keys.ENTER) this._opened = false;
			// this function logically should be called here. However, first half of code in dijit/form/_AutoCompleterMixin.js->_onKey()
			// require that this would have real value, so we have to set _opened to false only for code that process ESCAPE (ENTER and TAB fall through to the ESCAPE)
			// therefore the only good point to change it is in _selectOption() which luckily this module override, so I put it there

			this.inherited(arguments);

			// restore _opened
			if (key == keys.ENTER) this._opened = oldOpened;

			// don't process keys with modifiers  - but we want shift+TAB
			if (evt.altKey || evt.ctrlKey || evt.metaKey) {
				return;
			}

			switch (key) {
				case keys.ENTER:
					const {focusNode, hasPlaceholder, store} = this;

					if (!highlighted && hasPlaceholder && focusNode.value) {
						// start delayed search once text is entered and user hits ENTER
						// NOTE: there is similar code in SelectController to handle inline editing - this code is not used in that case
						store.skipDelayOnNextQuery = true;
						this._startSearchFromInput();
						evt.stopPropagation();
						evt.preventDefault();
						return;
					}
				case keys.INSERT:
					// prevent submitting form if user presses enter. Also
					// prevent accepting the value if either Next or Previous
					// are selected

					if (highlighted) {
						// only stop event on prev/next
						// prevent submit if ENTER was to choose an item
						evt.stopPropagation();
						evt.preventDefault();

						if (isNextPrevButton) {
							return;
						}
					} else {
						// Update 'value' (ex: KY) according to currently displayed text
						this._setBlurValue(); // set value if needed
						this._setCaretPos(this.focusNode, this.focusNode.value.length); // move cursor to end and cancel highlighting
					}

					if (key == keys.ENTER) {
						if (this.get("placeHolder") == "undefined") {
							this.set("placeHolder", this._getDefaultDisplayText());
						}
					} else {
						this.dropDown.selectNextNode();
					}

					evt.stopPropagation();
					evt.preventDefault();
					break;
				case keys.ESCAPE:
					if (this._opened) {
						this._lastQuery = null; // in case results come back later
						this.closeDropDown();
					} // let fall through to the tab
				case keys.TAB:
					// Fix for Safari, click on tab does not trigger blur event in Safari
					this._setBlurValue();
					break;
			}
		},

		_selectOption: function(/*DomNode*/ target) {
			if (target && this._highlightedKeyEvent == keys.ENTER) {
				this._announceOption(target);
				this._setCaretPos(this.focusNode, this.focusNode.value.length);
				// set _opened to false so the drop down wont be closed in _AutoCompleterMixin
				// we then restore opened in function _onKey - see comment there why we set _opened here
				this._opened = false;
			} else {
				this.inherited(arguments);
			}
		},

		_getValueAttr: function() {
			// summary:
			//		Hook for get('value') to work.

			// don't get the textbox value but rather the previously set hidden value.
			// Use this.valueNode.value which isn't always set for other MappedTextBox widgets until blur

			if (this.valueNode && this.valueNode.value == "") {
				this.displayedValue = this._getDefaultDisplayText();

				if (!this._initAtt) {
					this.set("placeHolder", this.displayedValue);
				}

				this.checkedItems = this.checkedItems || [];
			}

			this._initAtt = true;

			// in some cases this is initialized to an empty string, so convert to empty array
			if (!_.isArray(this.value) || (this.value && this.value.length == 1 && this.value[0] == this.CLEARVALUE)) {
				return [];
			}

			// make sure values are sorted before returning
			if (this.value.length > 1 && this._sortMap) {
				this.value = csi.sort(this.value, function(id) {
					return this[id] || "";
				}, this._sortMap);
			}

			return this.value;
		},

		_setBlurValue: function() {
			// summary: restore last valid value if blurred with invalid value

			if (this.bScanned) {
				this._startSearchFromInput();
				return;
			}

			// C8-63802 do nothing if value was not changed
			if (_.isEqual(this.value, this._lastValueReported)) {
				return;
			}

			//sort values so array comparison is done correct
			if (this.value) {
				this.value.sort();
			}
			this._lastValueReported = [].concat(this._lastValueReported).sort();

			// this code is similar to code in FilteringSelect, but uses "null" rather than "undefined"
			// so as to not trigger change from AutoCompleterMixin#_setBlurValue
			this.item = (this.item && Object.keys(this.item).length < 1) ? null : this.item;

			this.inherited(arguments);
			this.csiResetValue();
		},

		_setStoreAttr: function(store) {
			if (store) {
				this._newItemAttr = store.newProperty || "_newItem";

				// overriding store getIdentity as object passed may be undefined (dojo/store/Memory can have this issue)
				store.getIdentity = function(object) {
					return object ? object[this.idProperty] : null;
				};
			}

			this._defaultDisplayValueTotal = null;
			this._resultStore = null;
			this.inherited(arguments);
		},

		postMixInProperties: function() {
			this.value && (this.value = [].concat(this.value));
			this.checkedItems = this.value;
			this.exclusiveItems = this.exclusiveItems || {};
			this.inherited(arguments);
		},

		_postRequest: function() {
			this.inherited(arguments);

			if (this._resultStore) {
				var dfd = this._resultStore.query();
				var labelAttr = this.placeHolderLabelAttr || this.labelAttr || this.searchAttr;

				dfd.forEach(function(item) {
					if (this.store.exclusiveProperty && item[this.store.exclusiveProperty]) {
						this.exclusiveItems[item[this.store.idProperty]] = labelAttr ? item[labelAttr] : "";
					}

					if (this.initialSelection && this.value.indexOf(item[this.store.idProperty]) != -1) {
						this.checkedItems = _.union(this.checkedItems || [], [item[this.store.idProperty]]);
						this.checkedValues = _.union(this.checkedValues || [], labelAttr ? [item[labelAttr]] : []);
					}
				}, this);

				when(dfd, lang.hitch(this, function() {
					if (this.initialSelection && this.checkedItems) {
						this.value = _.uniq(this.checkedItems);
						this.initialSelection = false;
					}
				}));
			}

			if (this.defaultAll && (!this.store.isLoaded || this.store.isLoaded())) {
				//adds the data counts to default "All" on forms with defaultAll true
				var self = this;
				when(this.store.internalQuery(), function(results) {
					self._defaultDisplayValueTotal = results.total;
					self._setPlaceholderValue();
				});
			}
		},

		_setValueAttr: function( /*String|String[]*/ newValue, /*Boolean?*/ priorityChange, /*String?*/ displayedValue, /*item?*/ item, /* boolean? */ bResetSelected) {
			// summary:
			//		Hook so set('value', value) works.
			// description:
			//		Sets the value of the select.
			//		Also sets the label to the corresponding value by reverse lookup.
			var self = this;
			var store = this.store;
			var value = [];

			if (!this._onChangeActive) {
				priorityChange = null;
			}

			if (newValue) {
				value = value.concat(newValue);
			}

			if (this.dropDown) {
				this.checkedValues = this.dropDown.selectedValues;
				this.checkedItems = this.dropDown.value;
			}

			if ((value.length == 1) && (value[0] == csi.DIALOG_URL)) {
				// use case: dropdown have focus due to keyboard navigation and then user clicks on GoToDialog
				this.closeDropDown();
				this._handleOnChange(value[0], true);
			} else if (item === undefined) {
				if (!value.length) {
					this._csiResetValue = [];
					this.checkedItems = [];
					this.checkedValues = [];

					// when this function is called from resetSelected, value will always equal to this.value
					// so for GoToDialog case we have to call _handleOnChange anyway
					if (!_.isEqual(value, this.value) || (bResetSelected && this.preGoToDialogValue)) {
						//setting priorityChange to true will call onChange function if value set is not equal to value of the widget
						// don't change priority if dropDown is still on so it wont make server calls until onBlur
						priorityChange = this.dropDown && this.dropDown.selected ? false : true;
						this._handleOnChange(value, priorityChange);
					}
				} else if (store) {
					// I don't like this, but since _callbackSetLabel takes values one at a time, _lastValueReported is only set to one of the
					// values rather than the real value; to make sure the first change goes through, reset _lastValueReported so it is not
					// the same as the new first value. What matters here is that all values are the same, not just one.
					var lastValueReported = this._lastValueReported;
					this._lastValueReported = null;
					const _store = (store instanceof DelayedRequest) ? store.sourceStore : store;

					_.each(value, function(val) {
						when(_store.get(val), function(item) {
							self._callbackSetLabel(item ? [item] : [], undefined, undefined, priorityChange);
						});
					});

					this._lastValueReported = lastValueReported;
				}
			} else {
				if (this.dropDown) {
					this.valueNode.value = this.dropDown.value ? this.dropDown.value : "";
					this.value = this.checkedValues;
					this._csiResetValue = this.value;
				} else {
					this.valueNode.value = value;
				}

				if (this.textbox) {
					// Retrieve current text box value.
					var oldValue = this.textbox.value;
					this.inherited(arguments);
					if (!this.dropDown) {
						this.textbox.value = "";
					} else if (!this.resetTextOnSelect && oldValue) {
						// Don't reset if resetTextOnSelect set to false.
						this.textbox.value = oldValue;
						this.set("placeHolder", "");

						// select the input text, so user can delete it or continue typing.
						_TextBoxMixin.selectInputText(this.focusNode);
					}
				}

				this._bSettingItemValue = true;
			}

			// add class when value selected to override placeholder styling
			domClass.toggle(this.domNode, "csi-multiselect-value-selected", !!value.length);
		},

		_callbackSetLabel: function(
						/*Array*/ result,
						/*Object*/ query,
						/*Object*/ options,
						/*Boolean?*/ priorityChange) {
			// summary:
			//		Callback from dojo.store after lookup of user entered value finishes

			// setValue does a synchronous lookup,
			// so it calls _callbackSetLabel directly,
			// and so does not pass dataObject
			// still need to test against _lastQuery in case it came too late
			var resultId = result.length > 0 ? this.store.getIdentity(result[0]) : "";

			if ((query && query[this.searchAttr] !== this._lastQuery) || !query && result.length && !_.contains(this._lastQuery, resultId)) {
				if (result && result.length == 1) {
					this.set('item', result[0], priorityChange);
				}
				return;
			}

			if (result.length) {
				this.set('item', result[0], priorityChange);
			} else if (this.textbox) {
				// it is possible that this is called when the widget is first created and this.textbox does not yet exist;
				// in that case, both result and this.get("value") were empty arrays, so should not be a problem to skip this
				// statement in that case

				//#3268: don't modify display value on bad input
				//#3285: change CSS to indicate error
				this.set("value", '', priorityChange || (priorityChange === undefined && !this.focused), this.textbox.value, null);
			}
		},

		_setItemAttr: function( /*item*/ item, /*Boolean?*/ priorityChange, /*String?*/ displayedValue) {
			// summary: protect against javascript error if textbox does not exist
			if (this.textbox) {
				this.inherited(arguments);
			}
		},

		/** indicates if the "All" item is checked */
		allOptionIsChecked() {
			return this.get("value")?.includes?.(this.ALLVALUE);
		},
	});
});
